import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import {db} from '../../firebase';

interface Job {
  id: string;
  jobTitle: string;
  organization: string;
  dutyStation: string;
  description: string;
  responsibilities: string[];
  benefits: string[];
  requirements: string[];
  contactNumber: string;
  applicationDeadline: string;
  status: 'active' | 'inactive';
  createdAt?: any;
  updatedAt?: any;
}

const Container = styled.div`
  padding: 2rem;
  background-color: #f0f2f5;
  min-height: 100vh;
`;

const Header = styled.h1`
  color: #333;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #ddd;
  padding-bottom: 0.5rem;
`;

const SectionHeader = styled.h2`
  color: #555;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const FormContainer = styled.div`
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Textarea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
`;

const Select = styled.select`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const SubmitButton = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(SubmitButton)`
  background-color: #6c757d;

  &:hover {
    background-color: #5a6268;
  }
`;

const AddButton = styled(SubmitButton)`
  background-color: #28a745;

  &:hover {
    background-color: #218838;
  }
`;

const TableContainer = styled.div`
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5rem;
`;

const TableHead = styled.thead`
  background-color: #f8f9fa;
  color: #495057;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f2f2f2;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  margin-right: 0.5rem;
  font-size: 1rem;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: #6c757d;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #dc3545;

  &:hover {
    color: #c82333;
  }
`;

const ToggleStatusButton = styled(ActionButton)<{isActive: boolean}>`
  color: ${props => props.isActive ? '#dc3545' : '#28a745'};
  font-weight: bold;
`;

const Message = styled.p<{type: 'success' | 'error'}>`
  text-align: center;
  margin-top: 1rem;
  padding: 0.8rem;
  border-radius: 4px;
  color: white;
  background-color: ${props => props.type === 'success' ? '#28a745' : '#dc3545'};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const TagInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TagInputWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  min-height: 50px;
  align-items: center;
`;

const Tag = styled.span`
  background-color: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TagRemoveButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 0;
  font-size: 1rem;
`;

const TagTextInput = styled.input`
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  font-size: 1rem;
  padding: 0.25rem;
`;

const JobAdvertsManagement: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{text: string; type: 'success' | 'error'} | null>(null);
  const [tagInputs, setTagInputs] = useState({
    responsibilities: '',
    benefits: '',
    requirements: ''
  });

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      return new Date(timestamp).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'jobAdverts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const jobsData: Job[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        jobsData.push({
          id: doc.id,
          ...data,
          responsibilities: data.responsibilities || [],
          benefits: data.benefits || [],
          requirements: data.requirements || []
        } as Job);
      });
      setJobs(jobsData);
    } catch (error) {
      console.error('Error loading jobs: ', error);
      setMessage({text: 'Failed to load job adverts', type: 'error'});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const handleAddTag = (field: 'responsibilities' | 'benefits' | 'requirements', value: string) => {
    if (value.trim() && !currentJob?.[field].includes(value.trim())) {
      setCurrentJob(prev => ({
        ...prev!,
        [field]: [...prev![field], value.trim()]
      }));
      setTagInputs(prev => ({...prev, [field]: ''}));
    }
  };

  const handleRemoveTag = (field: 'responsibilities' | 'benefits' | 'requirements', index: number) => {
    setCurrentJob(prev => ({
      ...prev!,
      [field]: prev![field].filter((_, i) => i !== index)
    }));
  };

  const handleSaveJob = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!currentJob) return;

    setLoading(true);
    try {
      const jobData = {
        jobTitle: currentJob.jobTitle,
        organization: currentJob.organization,
        dutyStation: currentJob.dutyStation,
        description: currentJob.description,
        responsibilities: currentJob.responsibilities,
        benefits: currentJob.benefits,
        requirements: currentJob.requirements,
        contactNumber: currentJob.contactNumber,
        applicationDeadline: currentJob.applicationDeadline,
        status: currentJob.status,
        updatedAt: serverTimestamp()
      };

      if (currentJob.id) {
        const jobRef = doc(db, 'jobAdverts', currentJob.id);
        await updateDoc(jobRef, jobData);
        setMessage({text: 'Job advert updated successfully!', type: 'success'});
      } else {
        await addDoc(collection(db, 'jobAdverts'), {
          ...jobData,
          createdAt: serverTimestamp()
        });
        setMessage({text: 'Job advert added successfully!', type: 'success'});
      }

      await loadJobs();
      setCurrentJob(null);
      setIsFormVisible(false);
    } catch (error) {
      console.error('Error saving job: ', error);
      setMessage({text: 'Failed to save job advert', type: 'error'});
    } finally {
      setLoading(false);
    }
  };

  const handleEditJob = (job: Job) => {
    setCurrentJob(job);
    setIsFormVisible(true);
  };

  const handleDeleteJob = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this job advert?')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'jobAdverts', id));
        setJobs(jobs.filter(job => job.id !== id));
        setMessage({text: 'Job advert deleted successfully!', type: 'success'});
      } catch (error) {
        console.error('Error deleting job: ', error);
        setMessage({text: 'Failed to delete job advert', type: 'error'});
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddJob = () => {
    setCurrentJob({
      id: '',
      jobTitle: '',
      organization: '',
      dutyStation: '',
      description: '',
      responsibilities: [],
      benefits: [],
      requirements: [],
      contactNumber: '',
      applicationDeadline: '',
      status: 'active'
    });
    setIsFormVisible(true);
  };

  const handleCancelEdit = () => {
    setCurrentJob(null);
    setIsFormVisible(false);
    setTagInputs({responsibilities: '', benefits: '', requirements: ''});
  };

  const handleTagKeyDown = (field: 'responsibilities' | 'benefits' | 'requirements', e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInputs[field]) {
      e.preventDefault();
      handleAddTag(field, tagInputs[field]);
    }
  };

  return (
    <Container>
      <Header>Job Adverts Management</Header>

      {message && (
        <Message type={message.type} onClick={() => setMessage(null)} style={{cursor: 'pointer'}}>
          {message.text}
        </Message>
      )}

      <AddButton onClick={handleAddJob} disabled={loading}>
        + Add New Job Advert
      </AddButton>

      {isFormVisible && currentJob && (
        <FormContainer>
          <h3>{currentJob.id ? 'Edit Job Advert' : 'Add New Job Advert'}</h3>
          <Form onSubmit={handleSaveJob}>
            <FormGroup>
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                type="text"
                value={currentJob.jobTitle}
                onChange={(e) => setCurrentJob({...currentJob, jobTitle: e.target.value})}
                placeholder="Enter job title"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="organization">Organization</Label>
              <Input
                id="organization"
                type="text"
                value={currentJob.organization}
                onChange={(e) => setCurrentJob({...currentJob, organization: e.target.value})}
                placeholder="Enter organization name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="dutyStation">Duty Station</Label>
              <Input
                id="dutyStation"
                type="text"
                value={currentJob.dutyStation}
                onChange={(e) => setCurrentJob({...currentJob, dutyStation: e.target.value})}
                placeholder="Enter duty station/location"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={currentJob.description}
                onChange={(e) => setCurrentJob({...currentJob, description: e.target.value})}
                placeholder="Enter job description"
                required
              />
            </FormGroup>

            {(['responsibilities', 'benefits', 'requirements'] as const).map((field) => (
              <FormGroup key={field}>
                <Label htmlFor={field}>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
                <TagInputContainer>
                  <TagInputWrapper>
                    {currentJob[field].map((item, index) => (
                      <Tag key={index}>
                        {item}
                        <TagRemoveButton
                          type="button"
                          onClick={() => handleRemoveTag(field, index)}
                        >
                          ×
                        </TagRemoveButton>
                      </Tag>
                    ))}
                    <TagTextInput
                      type="text"
                      value={tagInputs[field]}
                      onChange={(e) => setTagInputs({...tagInputs, [field]: e.target.value})}
                      onKeyDown={(e) => handleTagKeyDown(field, e)}
                      placeholder={`Type and press Enter to add ${field}`}
                    />
                  </TagInputWrapper>
                </TagInputContainer>
              </FormGroup>
            ))}

            <FormGroup>
              <Label htmlFor="contactNumber">Contact Number</Label>
              <Input
                id="contactNumber"
                type="tel"
                value={currentJob.contactNumber}
                onChange={(e) => setCurrentJob({...currentJob, contactNumber: e.target.value})}
                placeholder="Enter contact number"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="applicationDeadline">Application Deadline</Label>
              <Input
                id="applicationDeadline"
                type="date"
                value={currentJob.applicationDeadline}
                onChange={(e) => setCurrentJob({...currentJob, applicationDeadline: e.target.value})}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="status">Status</Label>
              <Select
                id="status"
                value={currentJob.status}
                onChange={(e) => setCurrentJob({...currentJob, status: e.target.value as 'active' | 'inactive'})}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </FormGroup>

            <ButtonGroup>
              <CancelButton type="button" onClick={handleCancelEdit} disabled={loading}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : currentJob.id ? 'Update Job' : 'Add Job'}
              </SubmitButton>
            </ButtonGroup>
          </Form>
        </FormContainer>
      )}

      <TableContainer>
        {loading && !isFormVisible ? (
          <LoadingMessage>Loading job adverts...</LoadingMessage>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Job Title</TableHeader>
                <TableHeader>Organization</TableHeader>
                <TableHeader>Duty Station</TableHeader>
                <TableHeader>Deadline</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {jobs.length > 0 ? (
                jobs.map(job => (
                  <TableRow key={job.id}>
                    <TableCell>{job.jobTitle}</TableCell>
                    <TableCell>{job.organization}</TableCell>
                    <TableCell>{job.dutyStation}</TableCell>
                    <TableCell>{formatDate(job.applicationDeadline)}</TableCell>
                    <TableCell>
                      <span style={{
                        color: job.status === 'active' ? '#28a745' : '#dc3545',
                        fontWeight: 'bold'
                      }}>
                        {job.status.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ActionButton onClick={() => handleEditJob(job)} disabled={loading}>
                        Edit
                      </ActionButton>
                      <ActionButton onClick={() => handleDeleteJob(job.id)} disabled={loading}>
                        Delete
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} style={{textAlign: 'center'}}>No job adverts found.</TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
        )}
      </TableContainer>
    </Container>
  );
};

export default JobAdvertsManagement;